import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { neveroverpayApi } from './neveroverpay/neveroverpayApi';
import BrandMark from './neveroverpay/BrandMark';
import { flushWelcomeConsent } from './neveroverpay/NeverOverpayWelcome';
import './neveroverpay/neveroverpay.css';

const API_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
  ? process.env.REACT_APP_BACKEND_URL
  : '';

function safeNextFromHash(raw) {
  const value = (raw || '').trim();
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('://')) {
    return '/onboarding';
  }
  if (value.startsWith('/dashboard') || value.startsWith('/snagcart') || value.startsWith('/websites')) {
    return '/onboarding';
  }
  return value;
}

/**
 * Completes first-party Google OAuth after the backend redirects here with
 * `#access_token=...&next=/onboarding`. Stores the JWT like email/password login.
 *
 * Legacy Emergent `#session_id=` is no longer used (backend returns 410 unless
 * ALLOW_EMERGENT_GOOGLE_AUTH is explicitly enabled).
 */
const AuthCallback = ({ setUser }) => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = (window.location.hash || '').replace(/^#/, '');
    const params = new URLSearchParams(hash);
    const accessToken = params.get('access_token');
    const nextPath = safeNextFromHash(params.get('next'));

    // Clear the hash immediately so the JWT is not left in the address bar.
    window.history.replaceState({}, document.title, window.location.pathname + window.location.search);

    if (!accessToken) {
      toast.error('Google sign-in failed. Please try again.');
      navigate('/auth?google_error=1', { replace: true });
      return;
    }

    (async () => {
      try {
        localStorage.setItem('token', accessToken);
        const meRes = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: 'include',
        });
        if (meRes.ok) {
          const user = await meRes.json();
          if (setUser) setUser(user);
          await flushWelcomeConsent((kind) => neveroverpayApi.grantConsent(kind));
          toast.success('Signed in with Google!');
          navigate(nextPath, { replace: true });
        } else {
          localStorage.removeItem('token');
          toast.error('Google sign-in failed. Please try again.');
          navigate('/auth?google_error=1', { replace: true });
        }
      } catch (e) {
        localStorage.removeItem('token');
        toast.error('Google sign-in failed. Please try again.');
        navigate('/auth?google_error=1', { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="np-root" data-testid="google-auth-callback">
      <div className="np-wrap" style={{ paddingTop: 80 }}>
        <BrandMark />
        <p className="np-muted" style={{ marginTop: 16 }}>Signing you in…</p>
      </div>
    </div>
  );
};

export default AuthCallback;
